// client/src/pages/auth/Login.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Divider, Input, PageLoader } from '../../components/ui';
import { AuthBrand, AuthShell, FormError, PasswordField } from './authKit';

/**
 * Enquiry capture for visitors who have no account yet.
 *
 * It hands off to the user's mail client rather than an API: there is no
 * enquiry endpoint on the server, and a mailto is a destination that exists.
 */
const EnquiryForm = () => {
  const [enquiryData, setEnquiryData] = useState({
    name: '',
    phone: '',
    email: '',
    city: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleEnquiryChange = (e) => {
    const { name, value } = e.target;
    setEnquiryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const subject = `New Enquiry from ${enquiryData.name}`;
      const body = `Name: ${enquiryData.name}\nPhone: ${enquiryData.phone}\nEmail: ${enquiryData.email}\nCity: ${enquiryData.city}`;

      window.location.href = `mailto:hello@edumapping.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      // Nothing has been sent yet: the visitor still has to press send in
      // their mail app. Thanking them as if it had arrived meant enquiries
      // were silently lost whenever that step was skipped.
      toast.success('Your email app has opened with the enquiry filled in. Press send there to reach us at hello@edumapping.com.');
      setEnquiryData({ name: '', phone: '', email: '', city: '' });
      setShowForm(false);
    } catch (error) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showForm) {
    return (
      <Button variant="secondary" fullWidth onClick={() => setShowForm(true)}>
        Fill enquiry form
      </Button>
    );
  }

  return (
    <form onSubmit={handleEnquirySubmit} className="space-y-4">
      <Input
        label="Name"
        name="name"
        type="text"
        required
        value={enquiryData.name}
        onChange={handleEnquiryChange}
        placeholder="Your full name"
      />
      <Input
        label="Phone"
        name="phone"
        type="tel"
        required
        value={enquiryData.phone}
        onChange={handleEnquiryChange}
        placeholder="+91 1234567890"
      />
      <Input
        label="Email"
        name="email"
        type="email"
        required
        value={enquiryData.email}
        onChange={handleEnquiryChange}
        placeholder="your.email@example.com"
      />
      <Input
        label="City"
        name="city"
        type="text"
        required
        value={enquiryData.city}
        onChange={handleEnquiryChange}
        placeholder="Your city"
      />
      <div className="flex gap-2">
        <Button type="submit" loading={isSubmitting} className="flex-1">
          Submit enquiry
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setShowForm(false);
            setEnquiryData({ name: '', phone: '', email: '', city: '' });
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

/**
 * `altLink` is the cross-link between the college and school entrances. It is
 * rendered inside the card rather than by the wrapper pages, which would strand
 * it below a full-viewport screen.
 */
// The API's login identifier is an email *or* a phone number: the route builds
// `_loginId` from `identifier || email`, and authService treats anything
// without an `@` as a phone number. This
// screen used to declare the field `type="email"` and gate it behind an
// email-only regex, so the phone half of that contract was unreachable from the
// UI — a seeded account like +91-9876543210 could sign in through curl but not
// through the app.
//
// The server reads any common spelling of a phone number ("+91 98765 43210",
// "098765 43210"), so nothing is normalised here; the check only has to be
// loose enough to let a plausible phone number through and
// tight enough to still catch a typo'd email.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d][\d\s().-]{5,19}$/;

const Login = ({ isSchoolMode = false, altLink }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ProtectedRoute stores the page it bounced from as a path string
  // ("/jobs/12?tab=apply"). This used to read `from.pathname`, which a string
  // does not have, so every deep link landed on the dashboard after sign-in.
  // A location object is still accepted for any caller that passes one.
  const rawFrom = location.state?.from;
  const fromPath =
    typeof rawFrom === 'string'
      ? rawFrom
      : rawFrom?.pathname
        ? `${rawFrom.pathname}${rawFrom.search || ''}`
        : '';
  // Only an in-app path: never back to a sign-in or sign-up screen, and never
  // a protocol-relative URL that would leave the site.
  const from =
    fromPath.startsWith('/') && !fromPath.startsWith('//') && !/^\/(login|register)/.test(fromPath)
      ? fromPath
      : '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  if (isAuthenticated) {
    return (
      <AuthShell>
        <PageLoader label="Taking you to your dashboard" />
      </AuthShell>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const identifier = formData.email.trim();
    if (!identifier) {
      newErrors.email = 'Email or phone number is required';
    } else if (!EMAIL_RE.test(identifier) && !PHONE_RE.test(identifier)) {
      newErrors.email = 'Enter a valid email address or phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await login(formData.email.trim(), formData.password);
      navigate(from, { replace: true });
    } catch (error) {
      // The server's sentences are written for the person signing in (wrong
      // details, waiting for approval, institution not yet approved, too many
      // attempts), so they are shown as they are. Rewording them here by
      // keyword used to turn "your institution is pending approval" into
      // "your account is pending approval", which sent people to chase the
      // wrong thing.
      const status = error?.status || error?.response?.status;
      let message = error?.response?.data?.message || error?.message;
      if (!error?.response && /network|timeout/i.test(error?.message || '')) {
        message = 'We could not reach EduMapping. Check your internet connection and try again.';
      } else if (status >= 500) {
        message = 'Something went wrong on our side. Please try again in a minute.';
      }
      setErrors({ submit: message || 'Sign-in failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthBrand tagline="From classroom to career" />

      <Card>
        <h1 className="font-display text-2xl font-bold tracking-tight text-ink-950">
          Sign in to your account
        </h1>
        <p className="mt-2 text-sm text-ink-600">
          New here?{' '}
          <Link
            to={isSchoolMode ? '/register/school' : '/register/college'}
            className="font-medium text-ink-950 underline underline-offset-2 hover:text-saffron-700"
          >
            Create an account
          </Link>
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
          <FormError>{errors.submit}</FormError>

          <Input
            label="Email or phone number"
            name="email"
            type="text"
            inputMode="email"
            autoComplete="username"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            icon={UserIcon}
            placeholder="you@example.com or +91 9876543210"
          />

          <PasswordField
            label="Password"
            name="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            placeholder="Enter your password"
          />

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-ink-600 underline-offset-2 hover:text-ink-950 hover:underline"
            >
              Forgot your password?
            </Link>
          </div>

          <Button type="submit" fullWidth size="lg" loading={isLoading}>
            Sign in
          </Button>
        </form>

        {altLink && (
          <p className="mt-5 text-center text-sm text-ink-600">
            {altLink.prefix}{' '}
            <Link
              to={altLink.to}
              className="font-medium text-ink-950 underline underline-offset-2 hover:text-saffron-700"
            >
              {altLink.label}
            </Link>
          </p>
        )}

        {/* Outside the sign-in <form>: nesting one form inside another is
            invalid HTML, and the browser drops the inner one. */}
        <Divider className="my-6" />
        <p className="mb-4 text-center text-sm text-ink-600">
          Interested in learning more? Fill out our enquiry form
        </p>
        <EnquiryForm />
      </Card>
    </AuthShell>
  );
};

export default Login;
